import { Link as RouterLink } from 'react-router-dom';
import { Link, TableCell, TableContainer } from '@mui/material';
import DataEmptyMessage from '@components/common/DataEmptyMessage';
import MuiTable from '@components/common/MuiTable';
import DeliveryLocationCell from '@components/ContentComponents/Sessions/DeliveryLocationCell';
import DeliveryMethodCell from '@components/ContentComponents/Sessions/DeliveryMethodCell';
import { getStatusIcon } from '@components/Pages/Events/eventFunctions';
import StatusLegend from '@components/Pages/Events/Sessions/StatusLegend';
import { formatSessionDates, getDeliveryInfo, getSessionName } from '@components/Pages/Events/Sessions/sessionFunctions';
import { NILL } from '@lib/constants';
import moment from '@lib/moment';
import { defaultComparator } from '@lib/utils';

const headCells = [
  {
    id: 'date',
    label: 'Date & Time',
  },
  {
    id: 'name',
    label: 'Session Name',
  },
  {
    id: 'delivery_method',
    label: 'Delivery Method',
  },
  {
    id: 'delivery_site',
    label: 'Delivery Site & Address',
  },
  {
    id: 'status',
    label: 'Status',
  },
];

const nonNull = (val) => val || NILL;

/**
 * Returns a comparison function used for sorting rows.
 *
 * @param order
 * @param orderBy
 */
const getComparator = (order, orderBy) => {
  const ord = order === 'desc' ? -1 : 1;
  switch (orderBy) {
    case 'date':
      return (a, b) => {
        const aStart = a?.start ? moment(a.start) : null;
        const bStart = b?.start ? moment(b.start) : null;
        if (a === b) return 0;
        if (!aStart) return -1 * ord;
        if (!bStart) return 1 * ord;
        return aStart.diff(bStart) * ord;
      };
    case 'name':
      return (a, b) => getSessionName({ session: a }).localeCompare(getSessionName({ session: b })) * ord;
    case 'delivery_method':
      return (a, b) => (a.delivery_method ?? []).join('')
        .localeCompare((b.delivery_method ?? []).join('')) * ord;
    case 'delivery_site':
      return (a, b) => getDeliveryInfo(a).join('')
        .localeCompare(getDeliveryInfo(b).join('')) * ord;
    case 'status':
      return (a, b) => {
        return nonNull(a.status).localeCompare(nonNull(b.status)) * ord;
      };
    default:
      return defaultComparator;
  }
};

const EventSessionsTable = ({ event, sessions }) => {
  if (sessions.length > 0) {
    return (
      <>
        <StatusLegend sx={{ marginTop: '16px' }}/>
        <TableContainer data-cy="event-sessions-table">
          <MuiTable
            defaultOrderBy="date"
            headCells={headCells}
            data={sessions}
            getComparator={getComparator}
            alignTop
          >
            {(session) => (
              <>
                <TableCell
                  component="th"
                  scope="row"
                >
                  <Link to={`/events/${event.id}/${session.id}/`} component={RouterLink}>
                    {formatSessionDates(session.start, session.end)}
                  </Link>
                </TableCell>
                <TableCell align="left">{session.name}</TableCell>
                <DeliveryMethodCell session={session}/>
                <DeliveryLocationCell session={session}/>
                <TableCell align="left">{getStatusIcon(session.status)}</TableCell>
              </>
            )}
          </MuiTable>
        </TableContainer>
      </>
    )
  }

  return <DataEmptyMessage title={"There are no sessions for this event."}/>;

}

export default EventSessionsTable;
